import { Format, Routing } from "@common/_constants";
import { Button } from "@common/components/Atoms/Button";
import { Error } from "@common/components/Atoms/Error";
import {
    AddressLink,
    AttachmentLink,
    EmailLink,
    TelLink,
} from "@common/components/Atoms/Links";
import Loading from "@common/components/Atoms/Loading";
import LocalTime from "@common/components/Atoms/LocalTime";
import RelodeRep from "@common/components/Atoms/RelodeRep";
import { ActivityList } from "@common/components/Molecules/ActivitiesList";
import Detail from "@common/components/Molecules/Detail";
import { Activity, File as FileModel } from "@common/models";
import { GlobalConfig } from "@common/utils";
import { ReferralService } from "@pro/_services";
import MessageSummary from "@pro/components/Atoms/MessageSummary";
import {
    DetailActions,
    DetailSummary,
    DetailSummaryActions,
} from "@pro/components/Organisms/ReferralDetail";
import { DeclinePosition } from "@pro/containers/Modals/DeclinePosition";
import { EditReferral } from "@pro/containers/Modals/EditReferral";
import { ReferToAnotherJob } from "@pro/containers/Modals/ReferToAnotherJob";
import { SendMessage } from "@pro/containers/Modals/SendMessage";
import { PageInternal } from "@pro/containers/PageInternal";
import moment from "moment";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

interface ReferralProps extends RouteComponentProps<any> {}
interface ReferralState {
    candidate?: {
        job_candidate_id: number;
        user_address: string;
        user_email: string;
        user_phone_number: string;
        is_candidate_only: boolean;
        attachments: FileModel[];
        user_links: string[];
        job_id: number;
        job_name: string;
        status_name: string;
        hired_timestamp: string;
        date_applied: string;
        user_manager_profile_image_url: string;
        user_manager_name: string;
        user_manager_phone_number: string;
        user_manager_phone_number_for_url: string;
        user_full_name: string;
        user_id: number;
        total_messages: number;
        unread_messages: number;
        is_eligible_for_reject: boolean;
        resume_file: FileModel;
        job_candidate_user_id: number;
        user_manager_user_id: number;
        closest_interview_start_time: string;
        closest_interview_status_type_id: number;
    };
    error?: {
        message: string;
    };
    loading: boolean;
    isLargeScreen: boolean;
    editReferralModalIsOpen: boolean;
    sendMessageModalIsOpen: boolean;
    declinePositionModalIsOpen: boolean;
    referToAnotherJobModalIsOpen: boolean;
    meta: any[];
    activities: {
        results: Activity[];
    };
}

export class Referral extends React.Component<ReferralProps, ReferralState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.state = {
            candidate: undefined,
            error: undefined,
            loading: true,
            isLargeScreen: false,
            editReferralModalIsOpen: false,
            sendMessageModalIsOpen: false,
            declinePositionModalIsOpen: false,
            referToAnotherJobModalIsOpen: false,
            meta: [] as any[],
            activities: {
                results: [] as Activity[],
            },
        };

        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.onDeclineOkay = this.onDeclineOkay.bind(this);
        this.onEditReferralOkay = this.onEditReferralOkay.bind(this);
        this.onReferToAnotherJobOkay = this.onReferToAnotherJobOkay.bind(this);
        this.reloadPage = this.reloadPage.bind(this);
        this.fetchCandidate = this.fetchCandidate.bind(this);
        this.getActivityListing = this.getActivityListing.bind(this);
    }

    componentDidMount() {
        this.fetchCandidate(
            this.props.match.params.id,
            this.props.match.params.userid,
            this.props.match.params.jobid
        );
        this.setState({
            isLargeScreen: window.matchMedia(
                "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
            ).matches,
        });

        window.onresize = () => {
            this.setState({
                isLargeScreen: window.matchMedia(
                    "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
                ).matches,
            });
        };
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    openModal(modal: string) {
        const state = {};
        state[modal] = true;
        this.setState(state);
    }
    closeModal(modal: string) {
        const state = {};
        state[modal] = false;
        this.setState(state);
    }

    onDeclineOkay() {
        this.closeModal("declinePositionModalIsOpen");
        this.reloadPage();
    }

    onEditReferralOkay() {
        this.closeModal("editReferralModalIsOpen");
        this.reloadPage();
    }

    onReferToAnotherJobOkay(candidateId) {
        this.closeModal("referToAnotherJobModalIsOpen");
        this.props.history.push("/referrals/" + candidateId);
        this.fetchCandidate(candidateId);
    }

    reloadPage() {
        const candidate = this.state.candidate;
        const jobCandidateId =
            candidate && candidate.job_candidate_id
                ? candidate.job_candidate_id
                : this.props.match.params.id;

        this.fetchCandidate(jobCandidateId);
    }

    fetchCandidate(id: number, userid?: number, jobid?: number) {
        this.setState({ loading: true }, () => {
            this._abortControllers.getReferral = new AbortController();
            const getReferralSignal = this._abortControllers.getReferral.signal;
            ReferralService.getReferral(id, getReferralSignal, userid, jobid)
                .then(candidate => {
                    this._abortControllers.getActivitiesSummary = new AbortController();
                    const getActivitiesSummarySignal = this._abortControllers
                        .getActivitiesSummary.signal;

                    ReferralService.getActivitiesSummary(
                        candidate.job_candidate_id,
                        3,
                        getActivitiesSummarySignal
                    )
                        .then(activities => {
                            this.setState({
                                candidate,
                                activities,
                                loading: false,
                            });
                        })
                        .catch(error => {
                            if (
                                error.type === "abort" ||
                                error.name === "AbortError"
                            ) {
                                this.setState({ loading: false });
                                return;
                            }

                            this.setState(prevState => ({
                                error,
                                loading: false,
                            }));
                        });
                })
                .catch(error => {
                    if (error.type === "abort" || error.name === "AbortError") {
                        this.setState({ loading: false });
                        return;
                    }

                    this.setState(prevState => ({ error, loading: false }));
                });
        });
    }

    getActivityListing() {
        const acts = this.state.activities;

        if (!acts || !acts.results || acts.results.length === 0) {
            return <span>No activities in the last 90 days.</span>;
        }

        return (
            <ActivityList
                data={acts.results}
                classes="c-activity__list--collapsed"
            />
        );
    }

    render() {
        const {
            candidate,
            error,
            loading,
            isLargeScreen,
            declinePositionModalIsOpen,
            editReferralModalIsOpen,
            sendMessageModalIsOpen,
            referToAnotherJobModalIsOpen,
        } = this.state;
        const detailNav = ["overview"];

        if (loading) {
            return (
                <PageInternal size="md">
                    <Loading />
                </PageInternal>
            );
        }

        if (error) {
            return (
                <PageInternal size="md">
                    <Error show={!!error} description={error.message} />
                    <div className="c-block u-text-center">
                        <Link
                            className="c-button c-button--green"
                            to={Routing.Client.Pro.Jobs.SEARCH}
                        >
                            view jobs
                        </Link>
                    </div>
                </PageInternal>
            );
        }

        if (!candidate) return null;

        const detailMeta = {
            listItems: [] as any[],
            listItemClass: "referral-meta-list-item",
        };
        if (candidate.user_address) {
            detailMeta.listItems.push(
                <AddressLink icon={true} address={candidate.user_address} />
            );
        }
        if (candidate.user_email) {
            detailMeta.listItems.push(
                <EmailLink icon={true} email={candidate.user_email} />
            );
        }
        if (candidate.user_phone_number) {
            detailMeta.listItems.push(
                <TelLink icon={true} tel={candidate.user_phone_number} />
            );
        }

        if (!isLargeScreen) {
            detailNav.push("job");
        }

        if (!candidate.is_candidate_only) {
            detailNav.push("messages");
        }

        detailNav.push("recent activities");

        if (
            candidate.attachments.length > 0 ||
            candidate.user_links.length > 0
        ) {
            detailNav.push("attachments");
        }

        const detailSummaryActions = candidate.is_candidate_only ? (
            <br />
        ) : (
            <DetailSummaryActions
                openMessageModal={() =>
                    this.openModal("sendMessageModalIsOpen")
                }
                openEditReferralModal={() =>
                    this.openModal("editReferralModalIsOpen")
                }
            />
        );

        const detailActions = candidate.is_candidate_only ? (
            <span />
        ) : (
            <DetailActions
                openEditReferralModal={() =>
                    this.openModal("editReferralModalIsOpen")
                }
                linkName={"edit referral"}
            />
        );

        return (
            <PageInternal size="md" headerTitle={candidate.user_full_name}>
                <Detail
                    nav={detailNav}
                    title={candidate.user_full_name}
                    meta={detailMeta}
                    summary={
                        <DetailSummary
                            candidate={candidate}
                            detailSummaryActions={detailSummaryActions}
                        />
                    }
                    actions={detailActions}
                    id="overview"
                >
                    {!isLargeScreen && (
                        <div className="c-block" id="job">
                            <h2 className="c-block__title">job</h2>
                            <h3>
                                <Link
                                    to={"/jobs/" + candidate.job_id}
                                    className="c-link c-link--magenta"
                                >
                                    {candidate.job_name}
                                </Link>
                            </h3>
                            <ul className="c-list">
                                <li>{candidate.status_name}</li>
                                {!!candidate.closest_interview_start_time && (
                                    <li>
                                        Interview{" "}
                                        {candidate.closest_interview_start_time}
                                        {candidate.closest_interview_status_type_id ===
                                        10
                                            ? " (cancelled)"
                                            : ""}
                                    </li>
                                )}
                                {candidate.hired_timestamp && (
                                    <li>
                                        {moment
                                            .utc()
                                            .diff(
                                                moment(
                                                    candidate.hired_timestamp
                                                )
                                            ) > 0
                                            ? "Started "
                                            : "Starting "}
                                        <LocalTime
                                            utcTimestamp={
                                                candidate.hired_timestamp
                                            }
                                            format={
                                                Format.Datetime.DATE_ABBR_MONTH
                                            }
                                        />
                                    </li>
                                )}
                                <li>
                                    Applied{" "}
                                    <LocalTime
                                        utcTimestamp={candidate.date_applied}
                                    />
                                </li>
                            </ul>
                        </div>
                    )}

                    {!candidate.is_candidate_only && (
                        <MessageSummary
                            jobId={candidate.job_id}
                            userId={candidate.user_id}
                            total={candidate.total_messages}
                            unread={candidate.unread_messages}
                        />
                    )}

                    {!isLargeScreen && (
                        <div className="c-block">
                            <div className="c-block__title">questions?</div>
                            <RelodeRep
                                image={candidate.user_manager_profile_image_url}
                                name={candidate.user_manager_name}
                            >
                                <div className="c-link--questions-send-message">
                                    <a
                                        className="c-link c-link--magenta"
                                        onClick={() =>
                                            this.openModal(
                                                "sendMessageModalIsOpen"
                                            )
                                        }
                                    >
                                        send message
                                    </a>
                                </div>
                            </RelodeRep>
                        </div>
                    )}

                    <div id="recent-activities">
                        <h2>recent activities</h2>
                    </div>
                    {this.getActivityListing()}
                    <Link
                        className="c-link c-link--magenta"
                        to={`/referrals/${candidate.job_candidate_id}/activity`}
                    >
                        view all activity
                    </Link>

                    <div className="c-block" id="attachments">
                        <div className="c-block__title">attachments</div>
                        <div className="c-block__body">
                            {candidate.resume_file && (
                                <AttachmentLink
                                    key={candidate.resume_file.file_id}
                                    icon={true}
                                    url={candidate.resume_file.file_uri}
                                    label={
                                        candidate.resume_file.file_name +
                                        " (resume)"
                                    }
                                    type={candidate.resume_file.content_type}
                                    date={
                                        <LocalTime
                                            utcTimestamp={
                                                candidate.resume_file.timestamp
                                            }
                                        />
                                    }
                                    style={{ margin: "24px 0" }}
                                />
                            )}
                            {candidate.attachments.map((att, i) => (
                                <AttachmentLink
                                    key={i}
                                    icon={true}
                                    url={att.file_uri}
                                    label={att.file_name}
                                    type={att.content_type}
                                    date={
                                        <LocalTime
                                            utcTimestamp={att.timestamp}
                                        />
                                    }
                                    style={{ margin: "24px 0" }}
                                />
                            ))}
                            {candidate.user_links.map((link, i) => (
                                <AttachmentLink
                                    key={i}
                                    icon={true}
                                    url={link}
                                    label={link}
                                    type="web"
                                    style={{ margin: "24px 0" }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="c-block">
                        {candidate.is_eligible_for_reject && (
                            <Button
                                id="decline-position-button"
                                onClick={() =>
                                    this.openModal("declinePositionModalIsOpen")
                                }
                                style={{ margin: "0 24px 24px 0" }}
                            >
                                decline position
                            </Button>
                        )}
                        {!candidate.is_candidate_only && (
                            <Button
                                id="refer-another-button"
                                onClick={() =>
                                    this.openModal(
                                        "referToAnotherJobModalIsOpen"
                                    )
                                }
                                style={{ margin: "0 24px 24px 0" }}
                            >
                                refer to another job
                            </Button>
                        )}
                    </div>
                </Detail>

                <EditReferral
                    isOpen={editReferralModalIsOpen}
                    jobCandidateId={candidate.job_candidate_id}
                    onOkay={this.onEditReferralOkay}
                    onCancel={() => this.closeModal("editReferralModalIsOpen")}
                    closeModal={() =>
                        this.closeModal("editReferralModalIsOpen")
                    }
                />

                <SendMessage
                    isOpen={sendMessageModalIsOpen}
                    contextJobId={candidate.job_id}
                    contextJobName={candidate.job_name}
                    contextUserId={candidate.job_candidate_user_id}
                    contextUserFullName={candidate.user_full_name}
                    toUserIds={[candidate.user_manager_user_id]}
                    onCancel={() => this.closeModal("sendMessageModalIsOpen")}
                    closeModal={() => this.closeModal("sendMessageModalIsOpen")}
                />

                <ReferToAnotherJob
                    isOpen={referToAnotherJobModalIsOpen}
                    jobCandidate={candidate}
                    onOkay={this.onReferToAnotherJobOkay}
                    onCancel={() =>
                        this.closeModal("referToAnotherJobModalIsOpen")
                    }
                    closeModal={() =>
                        this.closeModal("referToAnotherJobModalIsOpen")
                    }
                />

                {candidate.is_eligible_for_reject && (
                    <DeclinePosition
                        isOpen={declinePositionModalIsOpen}
                        jobName={candidate.job_name}
                        jobCandidateId={candidate.job_candidate_id}
                        jobCandidateName={candidate.user_full_name}
                        onOkay={this.onDeclineOkay}
                        onCancel={() =>
                            this.closeModal("declinePositionModalIsOpen")
                        }
                        closeModal={() =>
                            this.closeModal("declinePositionModalIsOpen")
                        }
                    />
                )}
            </PageInternal>
        );
    }
}
